import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  CHART_CATEGORY_LABEL_LAYOUT,
  isAngledEffectiveMode,
} from '../shared/chart-tokens';
import type {
  ChartCategoryLabelLayout,
  EffectiveCategoryLabelMode,
} from '../shared/chart-tokens';

import {
  buildMarginCacheKey,
  getResolvedAxisMargin,
  setResolvedAxisMargin,
} from './bar-chart-layout-cache';
import type { ResolvedAxisMargins } from './bar-chart-layout-cache';
import { CHART_X_AXIS_TICK_LABEL_SELECTOR } from './bar-chart-tick-utils';
import {
  scheduleRetryingMeasure,
  useResizeMeasureSignal,
} from './use-bar-chart-measurement-scheduler';

const MEASURE_PAD_PX = 2;
const MARGIN_DELTA_THRESHOLD_PX = 1;
const ESTIMATED_TICK_CHARACTER_WIDTH_PX = 6.5;
const ESTIMATED_TICK_TEXT_HEIGHT_PX = 12;

export type { ResolvedAxisMargins };

/** Picks first, second, and longest tick labels to bound DOM overflow measurement cost. */
export const selectOverflowProbeNodes = <
  T extends { textContent: string | null }
>(
  tickLabels: readonly T[]
): T[] => {
  if (!tickLabels.length) {
    return [];
  }

  const firstNode = tickLabels[0];
  const secondNode = tickLabels.length > 1 ? tickLabels[1] : null;
  const longestNode = tickLabels.reduce((longest, node) => {
    const nodeLength = node.textContent?.trim().length ?? 0;
    const longestLength = longest.textContent?.trim().length ?? 0;

    return nodeLength > longestLength ? node : longest;
  }, tickLabels[0]);

  return Array.from(
    new Set(
      [firstNode, secondNode, longestNode].filter(
        (node): node is T => node !== null
      )
    )
  );
};

/** Adds measured overflow to current margins; never shrinks on resize or re-measure. */
export const computeGrowOnlyAxisMargins = (
  current: ResolvedAxisMargins,
  overflow: ResolvedAxisMargins
): { next: ResolvedAxisMargins; changed: boolean } => {
  const nextLeft =
    current.left +
    (overflow.left > MARGIN_DELTA_THRESHOLD_PX
      ? Math.ceil(overflow.left + MEASURE_PAD_PX)
      : 0);
  const nextBottom =
    current.bottom +
    (overflow.bottom > MARGIN_DELTA_THRESHOLD_PX
      ? Math.ceil(overflow.bottom + MEASURE_PAD_PX)
      : 0);
  const leftDelta = Math.abs(nextLeft - current.left);
  const bottomDelta = Math.abs(nextBottom - current.bottom);

  return {
    next: { left: nextLeft, bottom: nextBottom },
    changed:
      leftDelta > MARGIN_DELTA_THRESHOLD_PX ||
      bottomDelta > MARGIN_DELTA_THRESHOLD_PX,
  };
};

/**
 * Measures how far angled tick labels extend past the chart surface.
 * @returns `null` when the surface or tick labels are not yet in the DOM.
 */
export const resolveMeasuredOverflow = (
  chartRoot: HTMLElement
): ResolvedAxisMargins | null => {
  const surface = chartRoot.querySelector<SVGElement>('svg.recharts-surface');
  const tickLabels = Array.from(
    chartRoot.querySelectorAll<Element>(CHART_X_AXIS_TICK_LABEL_SELECTOR)
  );

  if (!surface || !tickLabels.length) {
    return null;
  }

  const probes = selectOverflowProbeNodes(tickLabels);
  const surfaceRect = surface.getBoundingClientRect();
  const bounds = probes.map((node) => node.getBoundingClientRect());
  const leftOverflow = Math.max(
    0,
    ...bounds.map((rect) => surfaceRect.left - rect.left)
  );
  const bottomOverflow = Math.max(
    0,
    ...bounds.map((rect) => rect.bottom - surfaceRect.bottom)
  );

  return { left: leftOverflow, bottom: bottomOverflow };
};

/** Estimates enough first-render bottom margin to avoid a visible short frame. */
export const estimateAngledAxisMarginBottom = (
  categories: readonly string[],
  layout: ChartCategoryLabelLayout
): number => {
  if (layout.angle === null) {
    return layout.marginBottom;
  }

  const longestLabelLength = categories.reduce(
    (longest, label) => Math.max(longest, label.trim().length),
    0
  );
  const estimatedLabelWidth =
    longestLabelLength * ESTIMATED_TICK_CHARACTER_WIDTH_PX;
  const estimatedRotatedHeight =
    Math.sin((Math.abs(layout.angle) * Math.PI) / 180) * estimatedLabelWidth +
    ESTIMATED_TICK_TEXT_HEIGHT_PX;
  const estimatedOverflow =
    estimatedRotatedHeight - layout.xAxisLabelHeight + MEASURE_PAD_PX;

  return Math.max(layout.marginBottom, Math.ceil(estimatedOverflow), 0);
};

interface UseAngledAxisMarginsOptions {
  categoryLabelMode: EffectiveCategoryLabelMode;
  categories: readonly string[];
  isValid: boolean;
  chartRootElement: HTMLDivElement | null;
  /** Caller-composed id for LRU warm-start (e.g. `${messageId}:${partIndex}` in chat). */
  cacheId?: string;
}

interface UseAngledAxisMarginsResult {
  marginLeft: number;
  marginBottom: number;
}

/**
 * Measures angled x-axis label overflow and returns grow-only left/bottom margins.
 * Warm-starts from the module LRU cache when `cacheId` is provided; re-measures on resize.
 */
export const useAngledAxisMargins = ({
  categoryLabelMode,
  categories,
  isValid,
  chartRootElement,
  cacheId,
}: UseAngledAxisMarginsOptions): UseAngledAxisMarginsResult => {
  const [measuredMargins, setMeasuredMargins] =
    useState<ResolvedAxisMargins | null>(null);
  const isAngledMode = isAngledEffectiveMode(categoryLabelMode);
  const modeLayout = CHART_CATEGORY_LABEL_LAYOUT[categoryLabelMode];
  const categoriesKey = useMemo(() => categories.join('\u0000'), [categories]);
  const marginCacheKey =
    cacheId && isAngledMode && categoriesKey
      ? buildMarginCacheKey(cacheId, categoryLabelMode, categoriesKey)
      : null;
  const cachedMargins =
    marginCacheKey !== null ? getResolvedAxisMargin(marginCacheKey) : null;
  const estimatedMarginBottom = estimateAngledAxisMarginBottom(
    categories,
    modeLayout
  );
  const marginLeft =
    measuredMargins?.left ?? cachedMargins?.left ?? modeLayout.marginLeft;
  const marginBottom =
    measuredMargins?.bottom ?? cachedMargins?.bottom ?? estimatedMarginBottom;

  useEffect(() => {
    setMeasuredMargins(null);
  }, [categoryLabelMode, categoriesKey, cacheId]);

  const measureRequestId = useResizeMeasureSignal({
    element: chartRootElement,
    enabled: isValid && isAngledMode,
  });

  useLayoutEffect(() => {
    const root = chartRootElement;

    if (!isValid || !isAngledMode || !root) {
      return;
    }

    return scheduleRetryingMeasure({
      measure: () => {
        const overflow = resolveMeasuredOverflow(root);

        if (!overflow) {
          return false;
        }

        const currentLeft =
          measuredMargins?.left ?? cachedMargins?.left ?? modeLayout.marginLeft;
        const currentBottom =
          measuredMargins?.bottom ??
          cachedMargins?.bottom ??
          estimatedMarginBottom;
        const { next, changed } = computeGrowOnlyAxisMargins(
          { left: currentLeft, bottom: currentBottom },
          overflow
        );

        if (!changed) {
          return true;
        }

        if (marginCacheKey !== null) {
          setResolvedAxisMargin(marginCacheKey, next);
        }
        setMeasuredMargins(next);

        return true;
      },
    });
  }, [
    isAngledMode,
    cachedMargins?.bottom,
    cachedMargins?.left,
    chartRootElement,
    isValid,
    marginCacheKey,
    measuredMargins,
    measureRequestId,
    estimatedMarginBottom,
    modeLayout.marginBottom,
    modeLayout.marginLeft,
  ]);

  return {
    marginLeft,
    marginBottom,
  };
};
